import type { LearnedLayoutSummary, LearningAccelerationReport } from './desktop-api';

type NamedType = { id: string; name: string };
const key = (name: string) => name.trim().toLocaleLowerCase();

/**
 * The names a type's layouts can be reported under. The layout index keeps the name a
 * type had when it was built, so after a rename the engine ties that older name to the
 * type's id (`typeIds`). A name the engine ties to a different type is never borrowed.
 */
export function layoutTypeNames(
  type: NamedType,
  report: LearningAccelerationReport | null | undefined,
): ReadonlySet<string> {
  const ids = Object.entries(report?.layouts?.typeIds ?? {}).map(
    ([name, id]) => [key(name), id] as const,
  );
  const own = key(type.name);
  const names = new Set<string>();
  if (!ids.some(([name, id]) => name === own && id !== type.id)) names.add(own);
  for (const [name, id] of ids) if (id === type.id) names.add(name);
  return names;
}

/** The shortlisted layouts that belong to the type. */
export function layoutsOfType(
  type: NamedType,
  report: LearningAccelerationReport | null | undefined,
): LearnedLayoutSummary[] {
  const names = layoutTypeNames(type, report);
  return (report?.layouts?.top ?? []).filter((layout) => names.has(key(layout.documentType)));
}
